import { useState, useMemo } from 'react';
import {
  CommandEmpty,
} from '@/components/ui/command';
import {
  SearchableDropdown,
  type SearchableDropdownProps,
} from '@/components/ui/searchable-dropdown';
import { DropdownTrigger } from '@/react/components/ui/dropdown-trigger';
import { DropdownListItem } from '@/react/components/ui/dropdown-list-item';
import { SearchableDropdownCreateButton } from '@/react/components/ui/searchable-dropdown-create-button';
import { useTranslation } from '@hooks/useTranslation';
import { FeeTypes, type OrderSource } from '@api/orderSources';
import { HighlightMatch } from '@components/common/HighlightMatch';

interface SourceComboboxProps {
  sources: OrderSource[];
  selectedId: string;
  onSelect: (id: string) => void;
  onCreateNew: () => void;
  // RBAC: hide the "create source" affordance for users without
  // order_sources.write. Defaults to true so existing callers (create order,
  // which is not gated) are unaffected.
  canWrite?: boolean;
  collisionPadding?: SearchableDropdownProps['collisionPadding'];
  invalid?: boolean;
}

function formatFee(source: OrderSource): string | null {
  if (!source.fee_type || source.fee == null) return null;
  if (source.fee_type === FeeTypes.PERCENTAGE) return `${source.fee}%`;
  return `$${source.fee}`;
}

export function SourceCombobox({
  sources,
  selectedId,
  onSelect,
  onCreateNew,
  canWrite = true,
  collisionPadding,
  invalid = false,
}: SourceComboboxProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedSource = sources.find((source) => String(source.id) === selectedId);

  const filteredSources = useMemo(() => {
    if (!searchQuery) return sources;
    const lowerQuery = searchQuery.toLowerCase();
    return sources.filter(
      (source) =>
        source.name.toLowerCase().includes(lowerQuery) ||
        source.description?.toLowerCase().includes(lowerQuery),
    );
  }, [sources, searchQuery]);

  return (
    <SearchableDropdown
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      searchPlaceholder={t('shared.search.source')}
      onSearchChange={setSearchQuery}
      shouldFilter={false}
      showSearchIcon={false}
      selectedValue={selectedId}
      collisionPadding={collisionPadding}
    >
      <SearchableDropdown.Trigger>
        <DropdownTrigger
          selectedLabel={selectedSource?.name}
          placeholder={t('create_order.placeholders.source')}
          isOpen={isOpen}
          invalid={invalid}
        />
      </SearchableDropdown.Trigger>

      <SearchableDropdown.List showFadeGradient maxHeight="256px">
        <CommandEmpty className="px-4 py-3 text-left text-sm text-grey">
          {t('shared.empty.no_sources')}
        </CommandEmpty>

        {filteredSources.length > 0 && (
          <div className="flex flex-col gap-2 py-2">
            {filteredSources.map((source) => {
              const fee = formatFee(source);
              const isSourceSelected = String(source.id) === selectedId;
              return (
                <DropdownListItem
                  key={source.id}
                  value={String(source.id)}
                  onSelect={() => {
                    onSelect(String(source.id));
                    setIsOpen(false);
                  }}
                  isSelected={isSourceSelected}
                  primary={<HighlightMatch text={source.name} query={searchQuery} />}
                  primaryTitle={source.name}
                  primarySuffix={fee ? `· ${fee}` : undefined}
                  secondary={source.description || undefined}
                  secondaryTitle={source.description || undefined}
                />
              );
            })}
          </div>
        )}
      </SearchableDropdown.List>

      {canWrite && (
        <SearchableDropdown.Footer>
          <SearchableDropdownCreateButton
            label={t('shared.create_new_source')}
            onSelect={() => { setIsOpen(false); onCreateNew(); }}
          />
        </SearchableDropdown.Footer>
      )}
    </SearchableDropdown>
  );
}
